import React from 'react'
import { useOffset, useYAxisScale } from 'recharts'
import { BUDGET_AXIS_BREAK_DISPLAY } from './budgetHistoryBudgetAxis'

const DOT_RADIUS = 2
/** Vertical spacing between dot centers (settings-icon ⋮ layout). */
const DOT_STACK_GAP = 5
/** Matches {@link BudgetHistoryCostChart} budget Y-axis tickMargin. */
export const BUDGET_BREAK_DOTS_TICK_MARGIN = 8
/** Budget axis uses `tickLine={false}`; label anchor sits at plot edge minus tick margin only. */
const BUDGET_AXIS_TICK_LINE_SIZE = 0
/** Nudge left from the tick label anchor so dots sit in the value column. */
export const BUDGET_BREAK_DOTS_LABEL_OFFSET_X = 14
const TICK_LABEL_DY = 4

type ThreeVerticalDotsProps = {
  cx: number
  cy: number
  fill: string
}

/** Three filled dots stacked vertically, like a settings / kebab icon. */
export const ThreeVerticalDots: React.FC<ThreeVerticalDotsProps> = ({ cx, cy, fill }) => (
  <g aria-hidden>
    {[-DOT_STACK_GAP, 0, DOT_STACK_GAP].map((dy) => (
      <circle key={dy} cx={cx} cy={cy + dy} r={DOT_RADIUS} fill={fill} />
    ))}
  </g>
)

type BudgetHistoryBreakDotsLayerProps = {
  yAxisId: string
  tickFill: string
}

/**
 * ⋮ break icon in the label column, vertically centered on the plot discontinuity
 * (same Y scale as budget series / grid).
 */
export const BudgetHistoryBreakDotsLayer: React.FC<BudgetHistoryBreakDotsLayerProps> = ({ yAxisId, tickFill }) => {
  const chartOffset = useOffset()
  const yScale = useYAxisScale(yAxisId)

  if (chartOffset == null || yScale == null) return null

  const yAtLowerTop = yScale(BUDGET_AXIS_BREAK_DISPLAY.lowerTop)
  const yAtUpperBottom = yScale(BUDGET_AXIS_BREAK_DISPLAY.upperBottom)
  if (yAtLowerTop == null || yAtUpperBottom == null) return null

  const cy = (yAtLowerTop + yAtUpperBottom) / 2
  const labelAnchorX = chartOffset.left - BUDGET_AXIS_TICK_LINE_SIZE - BUDGET_BREAK_DOTS_TICK_MARGIN
  const cx = labelAnchorX - BUDGET_BREAK_DOTS_LABEL_OFFSET_X

  return <ThreeVerticalDots cx={cx} cy={cy} fill={tickFill} />
}

type BudgetBreakAxisTickProps = {
  x?: number
  y?: number
  payload?: { value?: number }
  breakDisplayValue: number
}

type BudgetHistoryBudgetYAxisTickProps = BudgetBreakAxisTickProps & {
  formatTick: (value: number) => string
  tickFill: string
}

export const BudgetHistoryBudgetYAxisTick: React.FC<BudgetHistoryBudgetYAxisTickProps> = (props) => {
  const { payload, breakDisplayValue, formatTick, tickFill, x = 0, y = 0 } = props
  const value = payload?.value
  if (value == null || !Number.isFinite(value)) return null

  if (Math.abs(value - breakDisplayValue) < 0.001) {
    return null
  }

  const label = formatTick(value)
  if (!label) return null

  return (
    <text x={x} y={y} dy={TICK_LABEL_DY} textAnchor="end" fill={tickFill} fontSize={10}>
      {label}
    </text>
  )
}
